/**
 * `WorkflowCommandLine.ts` — the authored-view line format for one prepared
 * script command, and its inverse.
 *
 * A step's commands render in the editor as a numbered block, exactly like its
 * trigger buttons:
 *
 * ```
 * Commands:
 * 1. say Here is the fan assembly.
 * 2. hide_parts partPathsToHide: Robodog/Fan, Robodog/Cover
 * 3. wait_for trigger: delay, delaySeconds: 3
 * ```
 *
 * ## Why this needs its own module
 *
 * Everything else in the authored view is a `Key: value` line whose value is a
 * string. A command is a tool call whose `args` is an open, TYPED object, and
 * the view's parse-back is total — so a form that cannot express an argument
 * would DELETE it on the next save. That makes the round trip the whole problem,
 * and it is worth stating the rules that make it exact:
 *
 *  - **Every value's rendering is reversible.** A scalar renders bare when its
 *    text reads back as the same value, and QUOTED when it would not — an empty
 *    string, edge whitespace, an embedded `,` or `key:`, or a string that would
 *    otherwise read as a number or a boolean.
 *  - **Anything not a scalar or a list of scalars renders as JSON**, so a nested
 *    object survives instead of forcing the block to hide. There is no argument
 *    shape this cannot carry.
 *  - **Types come from the tool's schema when it is known, and from the value's
 *    own shape when it is not.** The editor always has the registry; a headless
 *    parse falls back to YAML's own scalar rules, which is what the file used.
 *  - **`say` takes the rest of the line, unquoted.** It is prose — it contains
 *    quotes and colons — and it has no arguments to delimit, so there is nothing
 *    to parse past it.
 *
 * The one shape that cannot round-trip is an entry the codec could not read at
 * all (`invalidRaw` — a non-mapping in `steps:`). Those are not rendered: see
 * `isCommandBlockRenderable`.
 *
 * Pure logic — no DOM, no Vue, no IOC.
 */

import { WorkflowScript, WorkflowScriptStep } from "./WorkflowDocument";
import { getScriptCommandSpec } from "@ai/registry/scriptCommandCatalog";

/** The `say` pseudo-command: a verbatim line, not a registered tool. */
export const SAY_COMMAND = "say";

/**
 * Start of an argument segment: a space, an identifier, a colon, a space (or
 * end of line). This is the ONLY delimiter, which is why a value containing one
 * must be quoted — see {@link needsQuoting}.
 */
const K_ARG_BOUNDARY = /\s+([A-Za-z_][A-Za-z0-9_]*):(?=\s|$)/g;

/** A bare token that would read back as a non-string. */
const K_NUMERIC = /^-?\d+(?:\.\d+)?$/;

function isBooleanText(text: string): boolean {
  return text === "true" || text === "false";
}

/** True when a rendered scalar would not read back as the same value. */
function needsQuoting(text: string): boolean {
  if (text.length === 0) return true;
  if (text !== text.trim()) return true;
  if (text.includes(",") || text.includes('"')) return true;
  if (K_NUMERIC.test(text) || isBooleanText(text)) return true;
  // An embedded `key:` token would be read as the next argument.
  K_ARG_BOUNDARY.lastIndex = 0;
  return K_ARG_BOUNDARY.test(` ${text}`);
}

function quote(text: string): string {
  return `"${text.replace(/"/g, '""')}"`;
}

function unquote(text: string): string {
  const trimmed = text.trim();
  if (trimmed.length < 2 || !trimmed.startsWith('"') || !trimmed.endsWith('"')) return null as never;
  return trimmed.slice(1, -1).replace(/""/g, '"');
}

function isQuoted(text: string): boolean {
  const trimmed = text.trim();
  return trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"');
}

/** Renders one scalar so that {@link readScalar} returns an equal value. */
function writeScalar(value: unknown): string {
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value === null) return "null";
  const text = String(value);
  return needsQuoting(text) ? quote(text) : text;
}

function isScalar(value: unknown): boolean {
  return (
    typeof value === "string" || typeof value === "number" || typeof value === "boolean"
  );
}

/**
 * Renders one argument's value.
 *
 * A scalar or a list of scalars gets the readable form the author edits; every
 * other shape falls back to JSON, which is what makes this format total — a
 * nested object round-trips instead of forcing the whole block to hide.
 */
function writeArgValue(value: unknown): string {
  if (Array.isArray(value) && value.every(isScalar)) {
    return value.length === 0 ? "[]" : value.map(writeScalar).join(", ");
  }
  if (isScalar(value) || value === null) return writeScalar(value);
  return JSON.stringify(value);
}

/**
 * Reads one scalar back.
 *
 * `declaredType` is the tool's own schema and wins when present. Without it —
 * a headless parse before the registry is built — the value's SHAPE decides,
 * which is exactly the rule the stored YAML was read with, so the fallback
 * cannot change a file's types behind the author's back.
 */
function readScalar(text: string, declaredType: string): unknown {
  if (isQuoted(text)) return unquote(text);

  const trimmed = text.trim();
  if (declaredType === "string") return trimmed;
  if (declaredType === "number" || declaredType === "integer") {
    return K_NUMERIC.test(trimmed) ? Number(trimmed) : trimmed;
  }
  if (declaredType === "boolean") {
    return isBooleanText(trimmed) ? trimmed === "true" : trimmed;
  }

  if (trimmed === "null") return null;
  if (isBooleanText(trimmed)) return trimmed === "true";
  if (K_NUMERIC.test(trimmed)) return Number(trimmed);
  return trimmed;
}

/**
 * Splits a list, leaving quoted items intact.
 *
 * **A bare comma is NOT a separator**, because a part hierarchy path is
 * `0,1,2` — commas are inside the values this format most often carries. The
 * separator is a comma at an item BOUNDARY: one followed by whitespace, or one
 * sitting against a quote. That is exactly what the renderer emits (`", "`),
 * and it means a hand-typed `partPathsToHide: 0,1, 0,2` reads as the two paths
 * the author meant rather than as four indices.
 */
function splitList(text: string): string[] {
  const items: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      // `""` inside a quoted run is an escaped quote, not a close.
      if (inQuotes && text[i + 1] === '"') {
        current += '""';
        i++;
        continue;
      }
      inQuotes = !inQuotes;
      current += ch;
      continue;
    }
    if (ch === "," && !inQuotes && isItemBoundary(text, i, current)) {
      items.push(current);
      current = "";
      continue;
    }
    current += ch;
  }
  items.push(current);
  return items.map((item) => item.trim()).filter((item) => item.length > 0);
}

/** True when the comma at `index` separates two items rather than sitting inside one. */
function isItemBoundary(text: string, index: number, current: string): boolean {
  const next = text[index + 1];
  if (next === undefined || /\s/.test(next)) return true;
  // `"a","b"` — against a closing or an opening quote.
  if (next === '"') return true;
  return current.trimEnd().endsWith('"');
}

function readArgValue(text: string, tool: string, argName: string): unknown {
  const trimmed = text.trim();
  const arg = getScriptCommandSpec(tool)?.properties.get(argName);
  const isList = arg?.type === "array";
  const itemType = arg?.itemType ?? "";
  const declaredType = arg?.type ?? "";

  if (trimmed === "[]") return [];
  if (!isQuoted(trimmed) && (trimmed.startsWith("{") || trimmed.startsWith("["))) {
    try {
      return JSON.parse(trimmed);
    } catch {
      // Not JSON after all — fall through and read it as text, so a value that
      // merely begins with a brace is not lost.
    }
  }

  if (isList) return splitList(trimmed).map((item) => readScalar(item, itemType));

  // No schema: a comma-separated run is a list, because that is the only thing
  // it could have been rendered from.
  if (declaredType === "" && trimmed.includes(",")) {
    return splitList(trimmed).map((item) => readScalar(item, ""));
  }

  return readScalar(trimmed, declaredType);
}

/**
 * One command as the text after `N. `, or null when it cannot be represented
 * (an entry the codec could not read).
 */
export function formatCommandEntry(command: WorkflowScriptStep): string | null {
  if (command.invalidRaw !== undefined) return null;

  if (command.tool.length === 0) {
    // `say` takes the rest of the line: prose carries quotes and colons, and
    // with no arguments to delimit there is nothing to parse past it.
    return `${SAY_COMMAND} ${command.sayText}`.trimEnd();
  }

  const args = command.args ?? {};
  const parts = Object.entries(args).map(
    ([key, value]) => `${key}: ${writeArgValue(value)}`,
  );
  return parts.length === 0 ? command.tool : `${command.tool} ${parts.join(" ")}`;
}

/** Maps one authored command entry (the text after `N. `) onto a script step. */
export function parseCommandEntry(text: string): WorkflowScriptStep {
  const step = new WorkflowScriptStep();
  const trimmed = text.trim();

  const head = /^([A-Za-z_][\w-]*)(?:\s+([\s\S]*))?$/.exec(trimmed);
  if (head === null) {
    // Not even a leading word: keep it as a say line rather than dropping it,
    // so nothing the author typed disappears without a trace.
    step.sayText = trimmed;
    return step;
  }

  const name = head[1];
  const rest = head[2] ?? "";

  if (name.toLowerCase() === SAY_COMMAND) {
    step.sayText = rest.trim();
    return step;
  }

  step.tool = name;
  const args = parseArgSegments(name, rest);
  step.args = Object.keys(args).length > 0 ? args : null;
  return step;
}

/**
 * One `<name>: <value>` segment of a command line's argument run.
 *
 * Offsets are into the text AFTER the tool name, and address the VALUE — which
 * is what the editor needs to put a picker on it. Exported so the editor's
 * decorations and this parse agree by construction: two scanners over the same
 * grammar would drift the first time the quoting rule changed.
 */
export interface CommandArgSegment {
  readonly name: string;
  /** Offset of the value's first character within `rest`. */
  readonly valueFrom: number;
  /** Offset just past the value within `rest`. */
  readonly valueTo: number;
}

/** Every argument segment in the text following a command's tool name. */
export function findArgSegments(rest: string): CommandArgSegment[] {
  const boundaries: Array<{ index: number; length: number; name: string }> = [];
  let inQuotes = false;
  // Padded so an argument at position 0 still has the leading space the
  // boundary pattern requires; offsets are corrected back on the way out.
  const padded = ` ${rest}`;
  for (let i = 0; i < padded.length; i++) {
    const ch = padded[i];
    if (ch === '"') {
      if (inQuotes && padded[i + 1] === '"') { i++; continue; }
      inQuotes = !inQuotes;
      continue;
    }
    if (inQuotes || !/\s/.test(ch)) continue;

    const match = /^\s+([A-Za-z_][A-Za-z0-9_]*):(?=\s|$)/.exec(padded.slice(i));
    if (match === null) continue;
    boundaries.push({ index: i, length: match[0].length, name: match[1] });
    i += match[0].length - 1;
  }

  return boundaries.map((boundary, index) => {
    const from = boundary.index + boundary.length;
    const to = index + 1 < boundaries.length ? boundaries[index + 1].index : padded.length;
    return { name: boundary.name, valueFrom: from - 1, valueTo: to - 1 };
  });
}

function parseArgSegments(tool: string, rest: string): Record<string, unknown> {
  const args: Record<string, unknown> = {};
  if (rest.trim().length === 0) return args;

  const segments = findArgSegments(rest);

  if (segments.length === 0) {
    // A bare remainder with no `key:` at all — the author is mid-typing, or the
    // command takes a single unnamed value. Keep it under the tool's first
    // declared argument when there is one, so it is not silently dropped.
    const first = [...(getScriptCommandSpec(tool)?.properties.keys() ?? [])][0];
    if (first !== undefined) args[first] = readArgValue(rest, tool, first);
    return args;
  }

  for (const segment of segments) {
    args[segment.name] = readArgValue(
      rest.slice(segment.valueFrom, segment.valueTo),
      tool,
      segment.name,
    );
  }

  return args;
}

/**
 * Whether a step's commands can be shown in the authored view at all.
 *
 * False when the script carries an entry the codec could not read: such an
 * entry has no line form, and rendering the block without it would delete it on
 * parse-back. The block is omitted instead, the whole script is carried across
 * the edit untouched, and `validate()` is what tells the author — which is the
 * panel's rule for a fact it can point at a row.
 */
export function isCommandBlockRenderable(script: WorkflowScript | null): boolean {
  if (script === null) return true;
  if (script.invalidRaw !== undefined) return false;
  return script.steps.every((step) => step.invalidRaw === undefined);
}

/** The script a step's `Commands:` block renders — its on-start one. */
export function findAuthoredScript(scripts: ReadonlyArray<WorkflowScript>): WorkflowScript | null {
  return scripts.find((script) => script.trigger === "on_start") ?? null;
}
